import React, { useState } from 'react';
import { hojeBR } from '../../utils/data';
import pedirSenhaAdmin from '../common/pedirSenhaAdmin';
import { calcularMultaJurosAtraso } from '../../utils/multaJurosAtraso';
import s from '../../pages/PedidosOrcamentos.module.css';

const fmtMoeda = v => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
const fmtData  = str => { if (!str) return '—'; return new Date(String(str).slice(0,10)+'T12:00:00').toLocaleDateString('pt-BR'); };

// Cartão e crédito do cliente ficam de fora — dependem de bandeira/parcelas
// ou de saldo por cliente, não combinam com "mesmos dados pra todos".
const FORMAS_LOTE = ['Dinheiro','PIX','Transferência','Boleto','Cheque','Outro'];

// Baixa em lote: mesmos dados (data/forma/conta) pra todos os títulos
// selecionados. Sem negociação manual de valor — cada título é quitado pelo
// nominal + multa/juros automáticos por atraso (se houver), calculado igual
// ao recebimento avulso; a única exceção é a isenção total via "Pagar sem
// multa/juros" (com senha), que zera o encargo do lote inteiro de uma vez.
// Título que precisa de desconto, valor diferente do calculado ou
// pagamento parcial deve ser recebido avulso, fora do lote.
export default function ModalBaixaLoteContaReceber({ titulos, contas, orgData, onConfirmar, onClose }) {
  const [f, setF] = useState({
    data_recebimento:  hojeBR(),
    forma_recebimento: 'Dinheiro',
    conta_id:          '',
  });
  const [registroUnico, setRegistroUnico] = useState(false);
  // "Pagar sem multa/juros" pro lote inteiro — mesma trava de senha do
  // recebimento avulso (ModalReceber), só que zera o encargo de todos os
  // títulos marcados de uma vez.
  const [semMultaJuros, setSemMultaJuros] = useState(false);
  const [senhaAjuste, setSenhaAjuste] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  // Sugestão "crua" (antes de aplicar a isenção) — decide se mostra a
  // checkbox e mostra no aviso amarelo quanto seria cobrado normalmente.
  const linhasBase = titulos.map(t => {
    const sugestao = !t.data_vencimento ? { multa: 0, juros: 0 }
      : calcularMultaJurosAtraso(t.valor, t.data_vencimento, f.data_recebimento, orgData);
    return { ...t, sugestao };
  });
  const totalEncargoBase = linhasBase.reduce((acc,l)=>acc+l.sugestao.multa+l.sugestao.juros, 0);
  const temEncargoBase = totalEncargoBase > 0.004;

  // Linhas efetivas — o que de fato vai ser cobrado (zeradas se isento)
  const linhas = linhasBase.map(l => {
    const sugestao = semMultaJuros ? { multa: 0, juros: 0 } : l.sugestao;
    const vLiquido = parseFloat(l.valor||0) + sugestao.multa + sugestao.juros;
    return { ...l, sugestao, vLiquido };
  });
  const totalNominal = linhas.reduce((acc,l)=>acc+parseFloat(l.valor||0), 0);
  const totalEncargo = linhas.reduce((acc,l)=>acc+l.sugestao.multa+l.sugestao.juros, 0);
  const totalLiquido = linhas.reduce((acc,l)=>acc+l.vLiquido, 0);

  const soBanco = ['pix','transferência','transferencia'].includes(f.forma_recebimento.toLowerCase());
  const contasFiltradas = soBanco ? contas.filter(c => c.tipo === 'banco') : contas;

  const alternarSemMultaJuros = async (marcar) => {
    if (!marcar) { setSemMultaJuros(false); return; }
    const senha = await pedirSenhaAdmin(
      'Pagar sem multa/juros exige senha de administrador ou gerente.',
      { acao: 'pagar_sem_multa_juros_lote',
        detalhes: `${titulos.length} título(s) — encargo total sugerido: ${fmtMoeda(totalEncargoBase)}` }
    );
    if (senha === null) return;
    setSenhaAjuste(senha);
    setSemMultaJuros(true);
  };

  const confirmar = async () => {
    if (!f.conta_id) return alert('Selecione a conta financeira.');
    setSalvando(true);
    try {
      await onConfirmar({
        ids: titulos.map(t => t.id),
        data_recebimento: f.data_recebimento,
        forma_recebimento: f.forma_recebimento,
        conta_id: f.conta_id,
        registro_unico: registroUnico,
        sem_multa_juros: semMultaJuros,
        senha_ajuste_manual: senhaAjuste || undefined,
      });
    } finally { setSalvando(false); }
  };

  return (
    <div className={s.confirmOverlay}>
      <div className={s.confirmBox} style={{width:620}}>
        <div className={s.confirmTitulo}>Baixar {titulos.length} título(s) selecionado(s)</div>
        <div className={s.confirmCorpo}>
          <div style={{maxHeight:220,overflowY:'auto',border:'1px solid #e5e7eb',borderRadius:4,marginBottom:12}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead style={{position:'sticky',top:0,background:'#eef2fa'}}>
                <tr>
                  <th style={{padding:'4px 6px',textAlign:'left'}}>Cód.</th>
                  <th style={{padding:'4px 6px',textAlign:'left'}}>Descrição</th>
                  <th style={{padding:'4px 6px',textAlign:'left'}}>Cliente</th>
                  <th style={{padding:'4px 6px',textAlign:'center'}}>Vencimento</th>
                  <th style={{padding:'4px 6px',textAlign:'right'}}>Valor</th>
                  <th style={{padding:'4px 6px',textAlign:'right'}}>Com encargo</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(l => (
                  <tr key={l.id} style={{borderTop:'1px solid #f0f0f0'}}>
                    <td style={{padding:'3px 6px'}}>{l.id}</td>
                    <td style={{padding:'3px 6px'}}>{l.descricao}</td>
                    <td style={{padding:'3px 6px'}}>{l.cliente_nome||'—'}</td>
                    <td style={{padding:'3px 6px',textAlign:'center'}}>{fmtData(l.data_vencimento)}</td>
                    <td style={{padding:'3px 6px',textAlign:'right'}}>{fmtMoeda(l.valor)}</td>
                    <td style={{padding:'3px 6px',textAlign:'right',
                      color: (l.sugestao.multa+l.sugestao.juros)>0.004 ? '#92400e' : 'inherit',
                      fontWeight: (l.sugestao.multa+l.sugestao.juros)>0.004 ? 600 : 400}}>
                      {fmtMoeda(l.vLiquido)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:'2px solid #dde3ef',fontWeight:700,background:'#f8fafc'}}>
                  <td colSpan={4} style={{padding:'4px 6px',textAlign:'right'}}>Total:</td>
                  <td style={{padding:'4px 6px',textAlign:'right'}}>{fmtMoeda(totalNominal)}</td>
                  <td style={{padding:'4px 6px',textAlign:'right'}}>{fmtMoeda(totalLiquido)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {temEncargoBase && !semMultaJuros && (
            <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:4,
              padding:'8px 12px',fontSize:12,color:'#92400e',marginBottom:8}}>
              ⚠️ {fmtMoeda(totalEncargoBase)} em multa/juros por atraso serão aplicados automaticamente
              (Parâmetros → Rotinas), somados ao total de cada título vencido.
            </div>
          )}
          {temEncargoBase && (
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginBottom:12}}>
              <input type="checkbox" checked={semMultaJuros}
                onChange={e=>alternarSemMultaJuros(e.target.checked)} />
              Pagar sem multa/juros — todo o lote (exige senha de admin/gerente)
            </label>
          )}
          {semMultaJuros && (
            <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:4,
              padding:'8px 12px',fontSize:12,color:'#166534',marginBottom:12}}>
              ✔️ Sem multa/juros — todos os títulos serão quitados só pelo valor nominal.
            </div>
          )}
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,width:150}}>
              Data do Recebimento
              <input type="date" className={s.inputErp} value={f.data_recebimento}
                onChange={e=>set('data_recebimento',e.target.value)} />
            </label>
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,width:170}}>
              Forma de Recebimento *
              <select className={s.selectErp} value={f.forma_recebimento}
                onChange={e=>set('forma_recebimento',e.target.value)}>
                {FORMAS_LOTE.map(fp=><option key={fp} value={fp}>{fp}</option>)}
              </select>
            </label>
            <label style={{display:'flex',flexDirection:'column',gap:3,fontSize:12,flex:1,minWidth:180}}>
              Conta Financeira * {soBanco && <span style={{color:'#1e40af'}}>(somente banco)</span>}
              <select className={s.selectErp} value={f.conta_id} onChange={e=>set('conta_id',e.target.value)}>
                <option value="">— Selecione —</option>
                {contasFiltradas.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginTop:12}}>
            <input type="checkbox" checked={registroUnico}
              onChange={e=>setRegistroUnico(e.target.checked)} />
            Gerar um único registro no financeiro (soma de {fmtMoeda(totalLiquido)}) — em vez de um
            lançamento por título
          </label>
        </div>
        <div className={s.confirmBtns}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmar} disabled={salvando}>
            {salvando ? 'Baixando...' : `Confirmar Baixa de ${fmtMoeda(totalLiquido)}`}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
